/**
 * Common interceptors for API requests and responses
 */

import type { RequestConfig, ApiResponse, RequestInterceptor, ResponseInterceptor } from '../types'

/**
 * Authentication interceptor that adds bearer token to requests
 */
export function createAuthInterceptor(getToken: () => string | null): RequestInterceptor {
  return {
    onFulfilled: config => {
      const token = getToken()
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      }
      return config
    },
  }
}

/**
 * Base URL interceptor
 */
export function createBaseUrlInterceptor(baseURL: string): RequestInterceptor {
  return {
    onFulfilled: config => {
      if (!config.baseURL && !isAbsoluteUrl(config.url)) {
        config.baseURL = baseURL
      }
      return config
    },
  }
}

/**
 * Request ID interceptor that adds unique request IDs
 */
export function createRequestIdInterceptor(): RequestInterceptor {
  return {
    onFulfilled: config => {
      config.headers = {
        ...config.headers,
        'X-Request-ID': generateRequestId(),
      }
      return config
    },
  }
}

/**
 * Content type interceptor
 */
export function createContentTypeInterceptor(contentType: string): RequestInterceptor {
  return {
    onFulfilled: config => {
      if (config.data && !config.headers?.['Content-Type'] && !config.headers?.['content-type']) {
        config.headers = {
          ...config.headers,
          'Content-Type': contentType,
        }
      }
      return config
    },
  }
}

/**
 * User agent interceptor
 */
export function createUserAgentInterceptor(userAgent: string): RequestInterceptor {
  return {
    onFulfilled: config => {
      config.headers = {
        ...config.headers,
        'User-Agent': userAgent,
      }
      return config
    },
  }
}

/**
 * API version interceptor
 */
export function createApiVersionInterceptor(
  version: string,
  headerName = 'X-API-Version'
): RequestInterceptor {
  return {
    onFulfilled: config => {
      config.headers = {
        ...config.headers,
        [headerName]: version,
      }
      return config
    },
  }
}

/**
 * Logging interceptor for requests
 */
export function createRequestLoggingInterceptor(
  logger: (message: string, data?: any) => void = console.log
): RequestInterceptor {
  return {
    onFulfilled: config => {
      logger('API Request', {
        method: config.method,
        url: config.url,
        headers: config.headers,
        data: config.data,
      })
      return config
    },
    onRejected: error => {
      logger('API Request Error', error)
      return Promise.reject(error)
    },
  }
}

/**
 * Logging interceptor for responses
 */
export function createResponseLoggingInterceptor(
  logger: (message: string, data?: any) => void = console.log
): ResponseInterceptor {
  return {
    onFulfilled: response => {
      logger('API Response', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: response.data,
      })
      return response
    },
    onRejected: error => {
      logger('API Response Error', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data,
      })
      return Promise.reject(error)
    },
  }
}

/**
 * Response transformation interceptor
 */
export function createResponseTransformInterceptor<T, U>(
  transformer: (data: T) => U
): ResponseInterceptor {
  return {
    onFulfilled: response => {
      response.data = transformer(response.data)
      return response
    },
  }
}

/**
 * Error handling interceptor
 */
export function createErrorHandlingInterceptor(
  errorHandler: (error: any) => any
): ResponseInterceptor {
  return {
    onRejected: error => {
      return errorHandler(error)
    },
  }
}

/**
 * Timeout interceptor
 */
export function createTimeoutInterceptor(timeout: number): RequestInterceptor {
  return {
    onFulfilled: config => {
      if (!config.timeout) {
        config.timeout = timeout
      }
      return config
    },
  }
}

/**
 * Cache control interceptor
 */
export function createCacheControlInterceptor(cacheControl: string): RequestInterceptor {
  return {
    onFulfilled: config => {
      config.headers = {
        ...config.headers,
        'Cache-Control': cacheControl,
      }
      return config
    },
  }
}

/**
 * CSRF token interceptor
 */
export function createCsrfInterceptor(
  getToken: () => string | null,
  headerName = 'X-CSRF-Token'
): RequestInterceptor {
  return {
    onFulfilled: config => {
      const token = getToken()
      if (
        token &&
        ['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase() || '')
      ) {
        config.headers = {
          ...config.headers,
          [headerName]: token,
        }
      }
      return config
    },
  }
}

/**
 * Rate limit handling interceptor
 */
export function createRateLimitInterceptor(
  onRateLimit?: (retryAfter: number) => void
): ResponseInterceptor {
  return {
    onRejected: error => {
      if (error.response?.status === 429) {
        const retryAfter = Number.parseInt(error.response.headers['retry-after'] || '60')
        if (onRateLimit) {
          onRateLimit(retryAfter)
        }
      }
      return Promise.reject(error)
    },
  }
}

/**
 * Response validation interceptor
 */
export function createResponseValidationInterceptor(
  validator: (data: any) => boolean,
  errorMessage = 'Invalid response data'
): ResponseInterceptor {
  return {
    onFulfilled: response => {
      if (!validator(response.data)) {
        throw new Error(errorMessage)
      }
      return response
    },
  }
}

/**
 * Request deduplication interceptor
 */
export function createDeduplicationInterceptor(): {
  requestInterceptor: RequestInterceptor
  responseInterceptor: ResponseInterceptor
} {
  const pendingRequests = new Map<string, Promise<ApiResponse>>()

  const generateKey = (config: RequestConfig): string => {
    return `${config.method || 'GET'}:${config.url}:${JSON.stringify(config.params || {})}`
  }

  return {
    requestInterceptor: {
      onFulfilled: config => {
        const key = generateKey(config)

        // Add request key to config for cleanup
        ;(config as any).__requestKey = key

        return config
      },
    },
    responseInterceptor: {
      onFulfilled: response => {
        const key = (response.config as any).__requestKey
        if (key) {
          pendingRequests.delete(key)
        }
        return response
      },
      onRejected: error => {
        const key = (error.config as any).__requestKey
        if (key) {
          pendingRequests.delete(key)
        }
        return Promise.reject(error)
      },
    },
  }
}

/**
 * Performance monitoring interceptor
 */
export function createPerformanceInterceptor(
  onMetrics?: (metrics: {
    url: string
    method: string
    duration: number
    status: number
  }) => void
): {
  requestInterceptor: RequestInterceptor
  responseInterceptor: ResponseInterceptor
} {
  return {
    requestInterceptor: {
      onFulfilled: config => {
        ;(config as any).__startTime = Date.now()
        return config
      },
    },
    responseInterceptor: {
      onFulfilled: response => {
        const startTime = (response.config as any).__startTime
        if (startTime && onMetrics) {
          onMetrics({
            url: response.config.url,
            method: response.config.method || 'GET',
            duration: Date.now() - startTime,
            status: response.status,
          })
        }
        return response
      },
      onRejected: error => {
        const startTime = (error.config as any).__startTime
        if (startTime && onMetrics) {
          onMetrics({
            url: error.config?.url || '',
            method: error.config?.method || 'GET',
            duration: Date.now() - startTime,
            status: error.response?.status || 0,
          })
        }
        return Promise.reject(error)
      },
    },
  }
}

/**
 * Helper function to check if URL is absolute
 */
function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//.test(url)
}

/**
 * Helper function to generate request ID
 */
function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}
